import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Button,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import brandBrain from "../assets/branding/brand-brain-mark.png";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
  },
  toolbar: {
    paddingRight: 24,
    minHeight: 70,
    paddingLeft: 18,
    [theme.breakpoints.down("sm")]: {
      paddingRight: 10,
      paddingLeft: 10,
      minHeight: 64,
    },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "12px 12px",
    minHeight: "72px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: "transparent",
    borderBottom: "none",
    backdropFilter: "blur(14px)",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 18,
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.85)",
    borderRadius: 16,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.20)"
        : "0 10px 24px rgba(15, 23, 42, 0.06)",
    [theme.breakpoints.down("sm")]: {
      marginRight: 10,
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    minHeight: 46,
  },
  brandWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gap: 8,
    },
  },
  brandIcon: {
    width: 42,
    height: 42,
    objectFit: "contain",
    borderRadius: 8,
    filter:
      theme.palette.type === "dark"
        ? "drop-shadow(0 10px 20px rgba(2, 6, 23, 0.26))"
        : "drop-shadow(0 10px 18px rgba(15, 23, 42, 0.10))",
    [theme.breakpoints.down("sm")]: {
      width: 34,
      height: 34,
    },
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    fontSize: 18,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      maxWidth: 110,
    },
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "82px",
    [theme.breakpoints.down("sm")]: {
      minHeight: "72px",
    },
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(12,18,25,0.98) 0%, rgba(15,23,32,0.98) 100%)"
        : "linear-gradient(180deg, rgba(246,248,250,0.98) 0%, rgba(241,245,248,0.98) 100%)",
  },
  contentBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.85)",
    borderRadius: 16,
    marginLeft: 8,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.18)"
        : "0 10px 24px rgba(15, 23, 42, 0.05)",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 6,
    },
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.10)"
        : "rgba(255, 255, 255, 0.88)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(71, 85, 105, 0.24)"
        : "rgba(148, 163, 184, 0.16)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 24px rgba(2, 6, 23, 0.16)"
        : "0 10px 24px rgba(15, 23, 42, 0.05)",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 6px",
      marginLeft: "auto",
    },
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  supportBanner: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(17, 57, 52, 0.72)"
        : "#f6fbf9",
    color: theme.palette.type === "dark" ? "#d8f5ee" : "#0c5f55",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(67, 156, 142, 0.28)"
        : "rgba(18, 140, 126, 0.14)"
    }`,
    padding: theme.spacing(1.75, 2.5),
    margin: theme.spacing(1.5, 2, 0),
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    boxShadow:
      theme.palette.type === "dark"
        ? "0 16px 30px rgba(2, 6, 23, 0.16)"
        : "0 16px 30px rgba(18, 140, 126, 0.06)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1, 1, 0),
      padding: theme.spacing(1.5),
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  supportBannerText: {
    flex: 1,
    lineHeight: 1.5,
    paddingRight: theme.spacing(1),
  }
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, stopSupportSession } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <MainListItems
            drawerClose={drawerClose}
            onOpenProfile={handleOpenUserModal}
            drawerOpen={drawerOpen}
          />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            <span className={classes.brandWrap}>
              <img
                src={brandBrain}
                alt="DeskAdvisor"
                className={classes.brandIcon}
              />
              <span className={classes.brandText}>DeskAdvisor</span>
            </span>
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && user.profile !== "superadmin" && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div className={classes.contentBody}>
        {user?.supportSession?.active ? (
          <div className={classes.supportBanner}>
            <Typography variant="body2" className={classes.supportBannerText}>
              Modo suporte ativo em{" "}
              <strong>{user.company?.name || "empresa"}</strong>. Sessão iniciada
              por <strong>{user.supportSession.impersonatedByEmail}</strong>.
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={stopSupportSession}
            >
              Encerrar modo suporte
            </Button>
          </div>
        ) : null}
        {children ? children : null}
        </div>
      </main>
    </div>
  );
};

export default LoggedInLayout;
